/**
 * StatusBadge · W124.2 · 2026-05-26
 *
 * Pílula compacta de status pra ser usada no header sticky da obra (e
 * potencialmente outros surfaces project-level). Mapeia o enum canônico
 * `ProjectStatus` (planning · in_progress · on_hold · completed · archived)
 * pros tokens semânticos do design system. Não passa por `Badge` porque
 * o hero sticky precisa de uma pílula menor (h-5) que o Badge "sm" (h-22)
 * — Badge é shared widget, mexer nele aqui arrastaria 30 outras telas.
 *
 * Cores seguem `OPERATING-MANUAL.md`:
 *   - `planning`     → cinza neutro (text-ink-muted)
 *   - `in_progress`  → ok (verde)
 *   - `on_hold`      → warn (âmbar)
 *   - `completed`    → ok solid (verde escuro)
 *   - `archived`     → muted (cinza fraco)
 */

import type { HTMLAttributes } from 'react';
import type { ProjectStatus } from '@builder/shared';
import { cn } from '@/lib/cn';
import { useT } from '@/hooks/useT';

interface StatusBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  status: ProjectStatus;
}

const STATUS_LABEL: Record<ProjectStatus, { pt: string; en: string }> = {
  planning:    { pt: 'Planejamento', en: 'Planning' },
  in_progress: { pt: 'Em andamento', en: 'In progress' },
  on_hold:     { pt: 'Pausada',      en: 'On hold' },
  completed:   { pt: 'Entregue',     en: 'Completed' },
  archived:    { pt: 'Arquivada',    en: 'Archived' },
};

const STATUS_TONE: Record<ProjectStatus, string> = {
  // muito leve no header — bg tintado + dot colorido + texto neutro/colorido
  planning: 'bg-surface-3 text-ink-muted border-line',
  in_progress: 'bg-ok/10 text-ok border-ok/25',
  on_hold: 'bg-warn/10 text-warn border-warn/25',
  completed: 'bg-ok/15 text-ok border-ok/35',
  archived: 'bg-surface-3/60 text-ink-subtle border-line',
};

const DOT_TONE: Record<ProjectStatus, string> = {
  planning: 'bg-ink-subtle',
  in_progress: 'bg-ok',
  on_hold: 'bg-warn',
  completed: 'bg-ok',
  archived: 'bg-ink-subtle',
};

export function StatusBadge({ status, className, ...rest }: StatusBadgeProps) {
  const { locale } = useT();
  // Degrade gracefully for unexpected/missing status values: fall back to the
  // neutral 'planning' tone/label instead of crashing on `undefined.pt`.
  const label = STATUS_LABEL[status] ?? STATUS_LABEL.planning;
  const tone = STATUS_TONE[status] ?? STATUS_TONE.planning;
  const dotTone = DOT_TONE[status] ?? DOT_TONE.planning;
  const displayLabel = locale === 'en' ? label.en : label.pt;
  return (
    <span
      data-status={status}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5',
        'text-[11px] font-medium leading-none whitespace-nowrap',
        tone,
        className,
      )}
      {...rest}
    >
      <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', dotTone)} />
      {displayLabel}
    </span>
  );
}
